import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, CheckCircle2, Eye, EyeOff, MessageSquareReply, MessageSquareOff, Search, Mail } from 'lucide-react';
import { messagesClientsApi } from '../../lib/api';
import { Button } from '../ui/Button';

type Destinataire = { id: string; company_name: string; email?: string };
type MessageEnvoye = {
    id: string; client_id: string; company_name: string; subject: string; message: string;
    reply_allowed: boolean; lu: boolean; reponses: number; status: string; created_at: string;
};

const champ = 'w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors';
const etiquette = 'text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block';

/** Interrupteur simple (titre + explication), pour les deux décisions du super admin. */
const Option = ({ actif, onChange, titre, aide }: { actif: boolean; onChange: (v: boolean) => void; titre: string; aide: string }) => (
    <button
        type="button"
        role="switch"
        aria-checked={actif}
        onClick={() => onChange(!actif)}
        className="w-full flex items-center justify-between gap-4 text-left rounded-xl border border-border bg-surface-soft px-4 py-3 hover:border-brand-500/50 transition-colors"
    >
        <span className="min-w-0">
            <span className="block text-sm font-semibold text-content">{titre}</span>
            <span className="block text-xs text-content-muted mt-0.5">{aide}</span>
        </span>
        <span className={`shrink-0 w-10 h-6 rounded-full p-0.5 transition-colors ${actif ? 'bg-brand-500' : 'bg-border'}`}>
            <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${actif ? 'translate-x-4' : ''}`} />
        </span>
    </button>
);

export function MessagesClientsView() {
    const navigate = useNavigate();
    const [destinataires, setDestinataires] = React.useState<Destinataire[]>([]);
    const [historique, setHistorique] = React.useState<MessageEnvoye[]>([]);
    const [choisis, setChoisis] = React.useState<string[]>([]);
    const [tous, setTous] = React.useState(false);
    const [recherche, setRecherche] = React.useState('');
    const [objet, setObjet] = React.useState('');
    const [texte, setTexte] = React.useState('');
    const [reponsePossible, setReponsePossible] = React.useState(true);
    const [prevenirParMail, setPrevenirParMail] = React.useState(true);
    const [envoi, setEnvoi] = React.useState(false);
    const [erreur, setErreur] = React.useState('');
    const [confirmation, setConfirmation] = React.useState('');

    const charger = React.useCallback(async () => {
        const [liste, hist] = await Promise.all([messagesClientsApi.destinataires(), messagesClientsApi.historique()]);
        setDestinataires(Array.isArray(liste) ? liste : []);
        setHistorique(Array.isArray(hist) ? hist : []);
    }, []);

    React.useEffect(() => { charger().catch(() => setErreur('Chargement impossible.')); }, [charger]);

    const visibles = destinataires.filter((d) => d.company_name?.toLowerCase().includes(recherche.trim().toLowerCase()));
    const nbDestinataires = tous ? destinataires.length : choisis.length;
    const peutEnvoyer = !!objet.trim() && !!texte.trim() && nbDestinataires > 0 && !envoi;

    const basculer = (id: string) => setChoisis((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const envoyer = async () => {
        setEnvoi(true);
        setErreur('');
        setConfirmation('');
        try {
            const res = await messagesClientsApi.envoyer({
                client_ids: tous ? 'all' : choisis,
                subject: objet.trim(),
                message: texte.trim(),
                reply_allowed: reponsePossible,
                send_email: prevenirParMail,
            });
            setConfirmation(`Message envoyé à ${res.envoyes} établissement${res.envoyes > 1 ? 's' : ''}${prevenirParMail ? ` — ${res.emails} e-mail${res.emails > 1 ? 's' : ''} d'avertissement` : ''}.`);
            setObjet('');
            setTexte('');
            setChoisis([]);
            setTous(false);
            await charger();
        } catch (e: any) {
            setErreur(e?.message || "L'envoi a échoué.");
        } finally {
            setEnvoi(false);
        }
    };

    return (
        <div className="space-y-6">
            <header>
                <h1 className="text-3xl font-bold tracking-tight text-content">Créer un message</h1>
                <p className="text-content-muted mt-1">
                    Écrivez à vos clients depuis la plateforme. Le message arrive dans leur module Support client.
                </p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <section className="lg:col-span-3 bg-surface border border-border rounded-2xl p-5 space-y-4">
                    <div>
                        <label className={etiquette}>Destinataires</label>
                        <label className="flex items-center gap-2 text-sm text-content mb-2 cursor-pointer">
                            <input type="checkbox" checked={tous} onChange={(e) => setTous(e.target.checked)} />
                            Tous les clients ({destinataires.length})
                        </label>
                        {!tous && (
                            <div className="rounded-xl border border-border bg-surface-soft">
                                <div className="flex items-center gap-2 px-3 border-b border-border">
                                    <Search size={14} className="text-content-muted" />
                                    <input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Chercher un établissement…"
                                        className="w-full bg-transparent py-2.5 text-sm text-content outline-none" />
                                </div>
                                <div className="max-h-48 overflow-y-auto p-1">
                                    {visibles.length === 0 && <p className="text-sm text-content-muted p-3">Aucun établissement.</p>}
                                    {visibles.map((d) => (
                                        <label key={d.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-content hover:bg-surface cursor-pointer">
                                            <input type="checkbox" checked={choisis.includes(d.id)} onChange={() => basculer(d.id)} />
                                            <span className="truncate">{d.company_name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                        <p className="text-xs text-content-muted mt-1.5">{nbDestinataires} destinataire{nbDestinataires > 1 ? 's' : ''} choisi{nbDestinataires > 1 ? 's' : ''}.</p>
                    </div>

                    <div>
                        <label className={etiquette}>Objet</label>
                        <input value={objet} onChange={(e) => setObjet(e.target.value)} maxLength={200} className={champ} placeholder="Ex. : Nouveautés de la plateforme" />
                    </div>
                    <div>
                        <label className={etiquette}>Message</label>
                        <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={7} maxLength={5000} className={`${champ} resize-none`}
                            placeholder="Bonjour {{entreprise}}, …" />
                        <p className="text-xs text-content-muted mt-1.5">
                            <code>{'{{entreprise}}'}</code> est remplacé par le nom de chaque établissement.
                        </p>
                    </div>

                    <Option actif={reponsePossible} onChange={setReponsePossible}
                        titre="Le client peut répondre"
                        aide={reponsePossible ? 'Il verra un champ de réponse sous votre message.' : "Message d'information : aucun champ de réponse."} />
                    <Option actif={prevenirParMail} onChange={setPrevenirParMail}
                        titre="Prévenir par e-mail"
                        aide="Le client reçoit un e-mail l'invitant à lire le message sur la plateforme (le texte n'est pas dans l'e-mail)." />

                    {erreur && <p className="text-sm text-red-500 font-semibold">{erreur}</p>}
                    {confirmation && (
                        <p className="text-sm text-emerald-600 font-semibold flex items-center gap-2"><CheckCircle2 size={16} /> {confirmation}</p>
                    )}
                    <div className="flex justify-end">
                        <Button size="md" variant="primary" onClick={envoyer} disabled={!peutEnvoyer}>
                            <Send size={15} /> {envoi ? 'Envoi…' : `Envoyer à ${nbDestinataires || '…'} client${nbDestinataires > 1 ? 's' : ''}`}
                        </Button>
                    </div>
                </section>

                <section className="lg:col-span-2 bg-surface border border-border rounded-2xl p-5">
                    <h2 className="text-sm font-bold text-content mb-1 flex items-center gap-2"><Mail size={15} /> Messages envoyés</h2>
                    <p className="text-xs text-content-muted mb-3">L'historique de tout ce que vous avez écrit à vos clients.</p>
                    <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                        {historique.length === 0 && <p className="text-sm text-content-muted py-6 text-center">Aucun message envoyé pour le moment.</p>}
                        {historique.map((m) => (
                            <button key={m.id} type="button" onClick={() => navigate('/admin/support')}
                                className="w-full text-left rounded-xl border border-border bg-surface-soft p-3 hover:border-brand-500/50 transition-colors">
                                <div className="flex items-start justify-between gap-2">
                                    <p className="text-sm font-bold text-content truncate">{m.subject}</p>
                                    <span className="text-[10px] text-content-muted shrink-0">{new Date(m.created_at).toLocaleDateString('fr-FR')}</span>
                                </div>
                                <p className="text-xs text-content-muted truncate">À {m.company_name}</p>
                                <p className="text-xs text-content-muted mt-1 line-clamp-2 whitespace-pre-wrap">{m.message}</p>
                                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-content-muted">
                                    <span className="inline-flex items-center gap-1">{m.lu ? <Eye size={12} /> : <EyeOff size={12} />}{m.lu ? 'Lu' : 'Non lu'}</span>
                                    <span className="inline-flex items-center gap-1">
                                        {m.reply_allowed ? <MessageSquareReply size={12} /> : <MessageSquareOff size={12} />}
                                        {m.reply_allowed ? (m.reponses > 0 ? `${m.reponses} réponse${m.reponses > 1 ? 's' : ''}` : 'Réponse possible') : 'Sans réponse'}
                                    </span>
                                </div>
                            </button>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
}
